/**
 * Change log view helpers (OP#1930) -- pure, unit-tested (auditView.test.ts).
 *
 * Backend: GET /api/v2/audit (api/v2/audit_log.py + services/audit_feed.py). The server decides
 * WHICH rows a role sees and scrubs every value (no secrets, allow-listed metadata); this module
 * only labels and formats what it was given. The CSV export is built from the SAME rows the
 * grid shows, so it can never carry more than the screen did (no second, unmasked fetch).
 *
 * Kept dependency-free (node --test runs it directly).
 */

/** One change in a row's before/after (the ConfigDiff change shape DiffView renders). */
export interface AuditChange {
  op: "add" | "remove" | "change";
  path: string;
  before?: unknown;
  after?: unknown;
  /** The value was masked server-side (a secret): render the masked marker, never a value. */
  secret?: boolean;
}

/** One change-log row as the server projects it for the caller's role. */
export interface AuditEntry {
  id: number;
  created_at: string | null;
  actor_type: string;
  actor_name: string | null;
  action: string;
  outcome: string;
  summary: string;
  target_type: string | null;
  target_id: string | null;
  target_label: string | null;
  org_id: number | null;
  site_id: number | null;
  site_name: string | null;
  before: unknown;
  after: unknown;
  changes: AuditChange[];
  changes_truncated: boolean;
  metadata: Record<string, unknown>;
  /** msp_admin and above only. */
  correlation_id?: string | null;
  actor_ip?: string | null;
  /** platform_admin only. */
  actor_id?: string | null;
}

export interface AuditPage {
  items: AuditEntry[];
  count: number;
  total: number;
  limit: number;
  next_cursor: string | null;
}

/** Hash-chain verification (platform_admin only): services/audit.verify_chain. */
export interface AuditChainStatus {
  ok: boolean;
  org_id: number;
  checked: number;
  broken_at: number | null;
  reason: string | null;
}

/** The in-page filters, as the user typed them. */
export interface AuditFilters {
  action?: string;
  actor?: string;
  serial?: string;
  outcome?: string;
  /** yyyy-mm-dd from a date input, interpreted as a UTC day. */
  from?: string;
  to?: string;
}

/**
 * Query params for GET /audit. Dates are whole UTC days (the page renders times in UTC, so a
 * "to" day includes everything up to 23:59:59.999Z of that day). Blank values are dropped.
 */
export function auditQuery(
  f: AuditFilters,
  scope: { orgId?: string | null; siteId?: string | null } = {},
): Record<string, string> {
  const q: Record<string, string> = {};
  const put = (k: string, v: string | null | undefined) => {
    const t = (v ?? "").trim();
    if (t) q[k] = t;
  };
  put("org_id", scope.orgId);
  put("site_id", scope.siteId);
  put("action", f.action);
  put("actor", f.actor);
  put("serial", f.serial);
  put("outcome", f.outcome);
  if (f.from && /^\d{4}-\d{2}-\d{2}$/.test(f.from)) q.since = `${f.from}T00:00:00Z`;
  if (f.to && /^\d{4}-\d{2}-\d{2}$/.test(f.to)) q.until = `${f.to}T23:59:59.999Z`;
  return q;
}

/**
 * Action families offered in the filter (prefix match server-side). Only families a customer
 * can actually see are listed; MSP-tier families are appended for msp_admin and above.
 */
export const ACTION_FAMILIES: { value: string; label: string }[] = [
  { value: "ssid.", label: "Wi-Fi networks" },
  { value: "radio.", label: "Radios" },
  { value: "psk.", label: "Personal keys (PSK)" },
  { value: "device.", label: "Devices" },
  { value: "switch.", label: "Switches" },
  { value: "site.", label: "Site settings" },
  { value: "passpoint.", label: "Passpoint" },
  { value: "portal.", label: "Guest portal" },
  { value: "voucher.", label: "Vouchers" },
  { value: "network_user.", label: "Network users" },
  { value: "notification_channel.", label: "Notifications" },
  { value: "firmware.", label: "Firmware" },
  { value: "gateway.", label: "Gateways" },
];
export const MSP_ACTION_FAMILIES: { value: string; label: string }[] = [
  { value: "operator.", label: "Team" },
  { value: "authz.", label: "Access denials" },
  { value: "ai.", label: "AI assistant" },
];

/** Exact labels for the actions a field user meets most. Anything else is derived. */
const ACTION_LABELS: Record<string, string> = {
  "ssid.upsert": "Wi-Fi network saved",
  "ssid.rename": "Wi-Fi network renamed",
  "ssid.delete": "Wi-Fi network deleted",
  "ssid.enable": "Wi-Fi network enabled",
  "ssid.disable": "Wi-Fi network disabled",
  "ssid.resync": "Wi-Fi networks re-synced",
  "ssid.evict": "Wi-Fi network removed from device",
  "device.rename": "Device renamed",
  "device.assign_site": "Device moved to site",
  "device.tags": "Device tags changed",
  "device.update": "Device updated",
  "device.description": "Device description changed",
  "device.port.set": "Device port changed",
  "device.port_profile.apply": "Port profile applied",
  "device.firmware.upgrade_issued": "Firmware upgrade started",
  "device.firmware.upgrade_completed": "Firmware upgrade completed",
  "device.firmware.upgrade_failed": "Firmware upgrade failed",
  "device.diagnostics.run": "Diagnostic run",
  "config.push": "Configuration pushed",
  "config.apply": "Configuration applied",
  "radio.update": "Radio settings changed",
  "radio.override": "Radio override set",
  "psk.add": "Personal key added",
  "psk.remove": "Personal key removed",
  "portal.set": "Guest portal enabled",
  "portal.disable": "Guest portal disabled",
  "passpoint.set": "Passpoint enabled",
  "passpoint.disable": "Passpoint disabled",
  "voucher.generate": "Vouchers generated",
  "voucher.revoke": "Voucher revoked",
  "switch.portconfig.apply": "Switch ports configured",
  "switch.port_name.set": "Switch port renamed",
  "switch.poe.action": "PoE action",
  "site.vlan_rollout.start": "VLAN rollout started",
  "site.vlan_rollout.finish": "VLAN rollout finished",
  "site.multicast.set": "Multicast setting changed",
  "site.mesh.create": "Mesh created",
  "site.mesh.update": "Mesh changed",
  "notification_channel.create": "Notification channel added",
  "notification_channel.update": "Notification channel changed",
  "notification_channel.delete": "Notification channel removed",
  "notification_channel.test": "Notification channel tested",
  "authz.denied": "Access denied",
  "operator.create": "Team member added",
  "operator.update": "Team member changed",
  "operator.delete": "Team member removed",
};

const NOUNS: Record<string, string> = {
  ssid: "Wi-Fi network", device: "Device", switch: "Switch", site: "Site", radio: "Radio",
  psk: "Personal key", voucher: "Voucher", portal: "Guest portal", passpoint: "Passpoint",
  network_user: "Network user", notification_channel: "Notification channel",
  operator: "Team member", firmware: "Firmware", gateway: "Gateway", config: "Configuration",
  ai: "AI assistant", authz: "Access", org: "Customer",
};

const sentence = (s: string) => s.replace(/[_.]+/g, " ").trim();

/** Human label for an action name ("site.vlan_rollout.switch" -> "Site: vlan rollout switch"). */
export function actionLabel(action: string): string {
  if (ACTION_LABELS[action]) return ACTION_LABELS[action];
  const [head, ...rest] = action.split(".");
  const noun = NOUNS[head ?? ""] ?? sentence(head ?? action);
  return rest.length ? `${noun}: ${sentence(rest.join("."))}` : noun;
}

/** Outcome -> the reserved status ramp (operational state is the sanctioned use). */
export function outcomeRamp(outcome: string): "healthy" | "warn" | "critical" | "info" {
  const o = outcome.toLowerCase();
  if (o === "success" || o === "ok" || o === "applied") return "healthy";
  if (o === "denied" || o === "failure" || o === "failed" || o === "error" || o === "rejected")
    return "critical";
  if (o === "partial" || o === "refused" || o === "skipped") return "warn";
  return "info";
}

/** What was changed, for the Target column: label, else type + id. */
export function targetText(e: Pick<AuditEntry, "target_label" | "target_type" | "target_id">): string {
  if (e.target_label) return e.target_label;
  if (e.target_type && e.target_id) return `${sentence(e.target_type)} ${e.target_id}`;
  return e.target_type ? sentence(e.target_type) : "—";
}

/** "2026-10-21 14:03:07 UTC" (the grid shows UTC so an export and a screenshot agree). */
export function fmtUtc(iso: string | null): string {
  if (!iso) return "—";
  const zoned = /(?:Z|[+-]\d{2}:?\d{2})$/.test(iso) ? iso : `${iso}Z`;
  const d = new Date(zoned);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toISOString().replace("T", " ").replace(/\.\d+Z$/, " UTC");
}

/**
 * Formula triggers a spreadsheet acts on (OP#1962): ASCII = + - @, their FULL-WIDTH forms
 * (U+FF1D ＝, U+FF0B ＋, U+FF0D －, U+FF20 ＠ -- Excel/LibreOffice in CJK locales normalise them)
 * and the small forms (U+FE66 ﹦, U+FE62 ﹢, U+FE63 ﹣, U+FE6B ﹫).
 */
const FORMULA_TRIGGER = "=+\\-@\\uFF1D\\uFF0B\\uFF0D\\uFF20\\uFE66\\uFE62\\uFE63\\uFE6B";
/**
 * A cell is dangerous when it STARTS with a control/whitespace character a spreadsheet strips
 * or treats as a cell break (tab, CR, LF -- the !310 guard missed LF), or when a trigger follows
 * any run of leading whitespace (JS \s also covers NBSP, U+3000 ideographic space, U+FEFF BOM):
 * "  =1+1" and "\n=HYPERLINK()" are formulas once the importer trims.
 */
const FORMULA_RE = new RegExp(`^(?:[\\t\\r\\n]|\\s*[${FORMULA_TRIGGER}])`);

/**
 * One CSV cell. Quoted per RFC 4180, and spreadsheet FORMULA INJECTION neutralised: a value
 * matching FORMULA_RE is prefixed with an apostrophe, because target labels and summaries are
 * operator-typed text and Excel would otherwise execute "=HYPERLINK(...)".
 */
export function csvCell(value: unknown): string {
  let s = value === null || value === undefined ? "" : typeof value === "string" ? value : JSON.stringify(value);
  if (FORMULA_RE.test(s)) s = `'${s}`;
  return /[",\r\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
}

export const CSV_COLUMNS = [
  "time_utc", "actor", "action", "action_label", "outcome", "site", "target", "summary", "changes",
] as const;

/**
 * CSV of the VISIBLE rows. Only server-projected fields are used -- the export can never hold
 * more than the grid (secrets are masked server-side; masked changes are written as the mask).
 */
export function toCsv(rows: AuditEntry[]): string {
  const lines = [CSV_COLUMNS.join(",")];
  for (const r of rows) {
    const changes = r.changes
      .map((c) =>
        c.secret
          ? `${c.op} ${c.path}: (secret - not shown)`
          : `${c.op} ${c.path}: ${c.op === "add" ? "" : `${JSON.stringify(c.before ?? null)} -> `}${
              c.op === "remove" ? "removed" : JSON.stringify(c.after ?? null)
            }`,
      )
      .join("; ");
    lines.push(
      [
        fmtUtc(r.created_at), r.actor_name ?? r.actor_type, r.action, actionLabel(r.action),
        r.outcome, r.site_name ?? (r.site_id ?? ""), targetText(r), r.summary, changes,
      ].map(csvCell).join(","),
    );
  }
  return lines.join("\r\n") + "\r\n";
}
